import Cookies from 'js-cookie';
import config from '@/lib/config';
import { COOKIES } from '@/lib/models';
import { lsClear } from '@/lib/utils/table-utils';
import { useCallback, useState } from 'react';
import { publicService } from '@/lib/services';
import { constructUrl } from '@/lib/utils';

const SIGN_OUT_COOKIE_KEYS = [
	COOKIES.TARGET_URL,
	COOKIES.INSIDER_SESSION,
	COOKIES.SP_SESSION,
	COOKIES.TM_SESSION,
	COOKIES.VD_SESSION,
	COOKIES.UI,
	COOKIES.SP_REMEMBER,
	COOKIES.TM_REMEMBER,
	COOKIES.INSIDER_REMEMBER,
	COOKIES.VD_REMEMBER,
];

const getDomainVariants = (domain: string) => {
	const trimmedDomain = domain.trim();
	if (!trimmedDomain) {
		return [];
	}

	const withoutLeadingDot = trimmedDomain.startsWith('.') ? trimmedDomain.slice(1) : trimmedDomain;
	const withLeadingDot = withoutLeadingDot ? `.${withoutLeadingDot}` : '';

	return Array.from(new Set([trimmedDomain, withoutLeadingDot, withLeadingDot].filter(Boolean)));
};

const removeCookieWithCommonScopes = (cookieKey: string, baseAttributes: Cookies.CookieAttributes = {}) => {
	Cookies.remove(cookieKey, baseAttributes);
	Cookies.remove(cookieKey, { ...baseAttributes, path: '/' });

	getDomainVariants(config.HACKETT_ADVISORY_COOKIE_DOMAIN).forEach((domain) => {
		Cookies.remove(cookieKey, { ...baseAttributes, domain });
		Cookies.remove(cookieKey, { ...baseAttributes, domain, path: '/' });
	});
};

// The login token cookie is HttpOnly, so only the backend can remove it (publicService.signOut)
const clearSignOutCookies = () => {
	SIGN_OUT_COOKIE_KEYS.forEach((cookieKey) => {
		removeCookieWithCommonScopes(cookieKey);
	});
};

export const useSignOut = () => {
	const [isSigningOut, setIsSigningOut] = useState(false);

	const getSignOutDestination = () => {
		return config.HACKETT_ADVISORY_USE_REAL_AUTH === 'true'
			? constructUrl(config.HACKETT_ADVISORY_AUTH_UI_URL, {
					redirectUrl: encodeURIComponent(`${window.location.href}`),
					platform: 'hackett-connect',
			  })
			: '/sign-in';
	};

	const signOut = useCallback(async () => {
		try {
			setIsSigningOut(true);

			await publicService.signOut().fetch();
		} catch (_error) {
			// Does not matter if signOut call fails.
		} finally {
			setIsSigningOut(false);

			clearSignOutCookies();
			lsClear();

			window.location.href = getSignOutDestination();
		}
	}, []);

	const signOutNoServiceCall = useCallback(async () => {
		clearSignOutCookies();
		lsClear();

		window.location.href = getSignOutDestination();
	}, []);

	return {
		signOut,
		signOutNoServiceCall,
		isSigningOut,
	};
};
